import React, { useState } from "react";
import {
  TableRow,
  TableCell,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Chip,
  Tooltip,
  Box,
  Typography,
} from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import DownloadIcon from "@material-ui/icons/Download";
import VisibilityIcon from "@material-ui/icons/Visibility";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ModalVerFacturaCliente from "./ModalVerFacturaCliente";
import { useSnackbar } from "notistack";
import PaymentBadge from "src/components/cliente/badges/PaymentBadge";
import { formatearFecha } from "../../../utils/formatTime";
import { makeStyles } from "@material-ui/styles";
import { descargarPdfDteProxy } from "src/utils/sharedFunctions";
import { useDescargarPdfDteMutation } from "src/services/dteApi";
import { formatAsCLP } from "src/utils/formatNumber";
import { useNavigate } from "react-router-dom";
import PaymentIcon from "@material-ui/icons/Payment";

const useStyles = makeStyles({
  tableCell: {
    verticalAlign: "middle",
  },
});

function FacturaRow({
  factura,
  tipo,
  seleccionada = false,
  onToggle,
  isMobile = false,
}) {
  const classes = useStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loadingDescargar, setLoadingDescargar] = useState(false);
  const [descargarPdf] = useDescargarPdfDteMutation();

  // Estados para el menú de acciones
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  // Estado para el modal de detalle
  const [openModal, setOpenModal] = useState(false);

  // Handlers del menú de acciones
  const handleAbrirMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCerrarMenu = () => {
    setAnchorEl(null);
  };

  const handleVerDetalle = () => {
    setOpenModal(true);
    handleCerrarMenu();
  };

  const handleCerrarModal = () => {
    setOpenModal(false);
  };

  const handlePagar = () => {
    handleCerrarMenu();
    const sufijo = tipo === "planes" ? "0" : "1";
    navigate(`/dashboard/pago/${factura.id}_${sufijo}`);
  };

  // Descarga el PDF por el proxy backend (la apikey vive solo en el backend).
  const handleDescargarPDF = async () => {
    handleCerrarMenu();
    setLoadingDescargar(true);
    try {
      // `tipo` es la prop del componente ("planes" | "tareas"); se mapea al tipo
      // que espera el backend en una variable local para no colisionar con ella.
      const tipoDte = tipo === "planes" ? "plan" : "tarea";
      await descargarPdfDteProxy({
        trigger: descargarPdf,
        tipo: tipoDte,
        id: factura.id,
        nombreArchivo: `Factura_${factura.folio}.pdf`,
        enqueueSnackbar,
      });
    } finally {
      setLoadingDescargar(false);
    }
  };

  const puedesPagar = ["FACTURADO", "ABONO"].includes(factura.estadoFactura);

  return (
    <TableRow
      hover
      tabIndex={-1}
      selected={seleccionada}
      style={{ verticalAlign: "middle" }}
    >
      {/* Columna: Checkbox */}
      <TableCell padding="checkbox">
        <Checkbox
          checked={seleccionada}
          disabled={!puedesPagar}
          onChange={() => onToggle && onToggle(factura)}
          color="primary"
          size="small"
        />
      </TableCell>

      {/* Columna: Tipo — oculta en mobile */}
      {!isMobile && (
        <TableCell className={classes.tableCell} align="left">
          <Chip
            label={tipo === "planes" ? "Plan" : "Tarea"}
            size="small"
            style={
              tipo === "planes"
                ? {
                    backgroundColor: "#E8F5E9",
                    color: "#2E7D32",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                  }
                : {
                    backgroundColor: "#E3F2FD",
                    color: "#1565C0",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                  }
            }
          />
        </TableCell>
      )}

      {/* Columna: Folio */}
      <TableCell align="left">
        {factura.folio}
        {isMobile && (
          <Typography variant="caption" display="block" color="textSecondary">
            {factura.nombreSociedad || "Sociedad sin nombre"} ·{" "}
            {factura.rutSociedad}
          </Typography>
        )}
      </TableCell>

      {/* Columna: Sociedad — en mobile se muestra bajo el folio */}
      {!isMobile && (
        <TableCell className={classes.tableCell} align="left">
          <Typography variant="body2">
            {factura.nombreSociedad || "Sociedad sin nombre"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {factura.rutSociedad}
          </Typography>
        </TableCell>
      )}

      {/* Columna: Glosa — oculta en mobile */}
      {!isMobile && (
        <TableCell className={classes.tableCell} align="left">
          {factura.detalleFactura || "—"}
        </TableCell>
      )}

      {/* Columna: Fecha Facturación — oculta en mobile */}
      {!isMobile && (
        <TableCell className={classes.tableCell} align="left">
          {factura.fechaFacturacion
            ? formatearFecha(
                factura.fechaFacturacion,
                "DD/MM/YYYY",
                "YYYY/MM/DD",
              )
            : "-"}
        </TableCell>
      )}

      {/* Columna: Periodo — oculta en mobile */}
      {!isMobile && (
        <TableCell className={classes.tableCell} align="left">
          {factura.fechaFacturacion
            ? formatearFecha(factura.fecha, "MMMM [del] YYYY", "YYYY/MM")
            : "-"}
        </TableCell>
      )}

      {/* Columna: Valor/Total */}
      <TableCell className={classes.tableCell} align="right">
        {factura.estadoFactura === "ABONO" ? (
          <Tooltip
            title={`Total factura: ${formatAsCLP(factura.total)} · Abono previo: ${formatAsCLP(factura.total - factura.montoRestante)}`}
            arrow
          >
            <Box
              display="inline-flex"
              alignItems="center"
              style={{ gap: 4, cursor: "help" }}
            >
              <Typography
                variant="body2"
                style={{ color: "#f59e0b", fontWeight: 600 }}
              >
                {formatAsCLP(factura.montoVisible)}
              </Typography>
              <InfoOutlinedIcon style={{ fontSize: 14, color: "#f59e0b" }} />
            </Box>
          </Tooltip>
        ) : (
          formatAsCLP(factura.montoVisible)
        )}
      </TableCell>

      {/* Columna: Estado */}
      <TableCell className={classes.tableCell} align="left">
        <PaymentBadge
          estado={factura.estadoFactura}
          fechaFacturacion={factura.fechaFacturacion}
        />
      </TableCell>

      {/* Columna: Fecha Pago — oculta en mobile */}
      {!isMobile && (
        <TableCell className={classes.tableCell} align="left">
          {factura.fechaPago ? factura.fechaPago : "-"}
        </TableCell>
      )}

      {/* Columna: Acciones */}
      <TableCell
        align="center"
        style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}
      >
        <IconButton
          size="small"
          onClick={handleAbrirMenu}
          aria-label="Acciones"
          aria-controls={openMenu ? "menu-acciones" : undefined}
          aria-haspopup="true"
          aria-expanded={openMenu ? "true" : undefined}
        >
          <MoreVertIcon />
        </IconButton>

        {/* Menú de acciones */}
        <Menu
          id="menu-acciones"
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleCerrarMenu}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          {/* Opción: Pagar con WebPay — solo facturas no pagadas */}
          {puedesPagar && (
            <MenuItem onClick={handlePagar}>
              <ListItemIcon>
                <PaymentIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Pagar con WebPay" />
            </MenuItem>
          )}
          {/* Opción: Ver detalle */}
          <MenuItem onClick={handleVerDetalle}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Ver detalle" />
          </MenuItem>

          {/* Opción: Descargar PDF */}
          <MenuItem
            onClick={handleDescargarPDF}
            disabled={!factura.documentoDisponible || loadingDescargar}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={loadingDescargar ? "Descargando..." : "Descargar PDF"}
            />
          </MenuItem>
        </Menu>
      </TableCell>

      {/* Modal para ver detalle de la factura */}
      <ModalVerFacturaCliente
        open={openModal}
        onClose={handleCerrarModal}
        factura={factura}
        tipo={tipo}
      />
    </TableRow>
  );
}

export default React.memo(FacturaRow);
